/**
 * Canonical Panini FIFA World Cup 2026 album section order.
 *
 * SINGLE SOURCE OF TRUTH for the JS side. Mirrors the backend module
 * `backend/order_services/album_order.py::ALBUM_SECTION_ORDER` byte-for-
 * byte. Any consumer that needs to sort sticker codes in "album order"
 * (the picking checklist, the Missing Stickers quote formatter, the
 * fulfillment invoice list, etc.) must import from THIS module — never
 * duplicate the array or invent a new sort routine.
 *
 * Rules:
 *   1. `FWC` (World Cup preface) is first.
 *   2. Country prefixes follow the exact publishing sequence below.
 *   3. Within a section, sort by numeric suffix ascending. So `CZE-3`
 *      comes before `CZE-18` and `FWC-00` comes before `FWC-1`.
 *   4. Unknown / malformed codes trail the canonical list, sorted
 *      alphabetically by raw code, so the sort NEVER crashes on
 *      legacy data.
 *
 * Owner-approved Aug 2026. Do NOT reorder or extend without updating
 * both the backend module and this file together.
 */

export const ALBUM_SECTION_ORDER = [
  "FWC", "MEX", "RSA", "KOR", "CZE", "CAN", "BIH", "QAT", "SUI", "BRA",
  "MAR", "HAI", "SCO", "USA", "PAR", "AUS", "TUR", "GER", "CUW", "CIV",
  "ECU", "NED", "JPN", "SWE", "TUN", "BEL", "EGY", "IRN", "NZL", "ESP",
  "CPV", "KSA", "URU", "FRA", "SEN", "IRQ", "NOR", "ARG", "ALG", "AUT",
  "JOR", "POR", "COD", "UZB", "COL", "ENG", "CRO", "GHA", "PAN", "CC",
];

const _RANK = new Map(ALBUM_SECTION_ORDER.map((p, i) => [p, i]));
const _UNKNOWN_RANK = ALBUM_SECTION_ORDER.length + 1;

// Accepts `PREFIX-NUMBER` (canonical) or `PREFIXNUMBER` (master-price-
// list variant) — matches the backend `_CODE_RE`.
const _CODE_RE = /^([A-Z]{2,15})-?(\d+)$/;

/**
 * Parse a sticker code into its `[prefix, number]` components.
 * Returns `[null, null]` when the code doesn't match the canonical
 * shape — callers should treat that as "unknown, sort last".
 * @param {string|null|undefined} code
 * @returns {[string|null, number|null]}
 */
export function parseCode(code) {
  if (!code) return [null, null];
  const m = _CODE_RE.exec(String(code).trim().toUpperCase());
  if (!m) return [null, null];
  return [m[1], Number.parseInt(m[2], 10)];
}

/**
 * Album-order rank for a bare country/prefix code, e.g. `"BRA" → 9`,
 * `"FWC" → 0`, `"CC" → 49`. Unknown codes get a sentinel rank AFTER
 * the entire canonical list so they trail without crashing the sort.
 * @param {string|null|undefined} prefix
 * @returns {number}
 */
export function getAlbumOrderIndex(prefix) {
  if (!prefix) return _UNKNOWN_RANK;
  const r = _RANK.get(String(prefix).trim().toUpperCase());
  return r === undefined ? _UNKNOWN_RANK : r;
}

/**
 * Sort key for `Array.prototype.sort` — returns a comparable tuple
 * `[sectionRank, stickerNumber, raw]`. Mirrors the backend
 * `album_sort_key`.
 * @param {string|null|undefined} code
 * @returns {[number, number, string]}
 */
export function albumSortKey(code) {
  const [pfx, num] = parseCode(code);
  const rank = getAlbumOrderIndex(pfx);
  if (rank === _UNKNOWN_RANK) return [rank, -1, String(code || "").toUpperCase()];
  return [rank, num == null ? -1 : num, String(code || "").toUpperCase()];
}

/**
 * Compare two sticker codes in canonical album order. Suitable for
 * `Array.prototype.sort` directly:
 *
 *   codes.sort(compareAlbumOrder);
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function compareAlbumOrder(a, b) {
  const ka = albumSortKey(a);
  const kb = albumSortKey(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] === kb[i]) continue;
    if (typeof ka[i] === "number" && typeof kb[i] === "number") return ka[i] - kb[i];
    return String(ka[i]).localeCompare(String(kb[i]));
  }
  return 0;
}
